// Beleidsmaker — singleton policy documents (PLAN-W27 A; FRONTEND-BLUEPRINT §3.2;
// tenant-beleidsmaker/snapshots/tenant-beleidsmaker-missie.txt). The RARE singleton surface:
// one document per (tenant, kind[, regulation]) — the page IS the entity, no list/detail. One
// shared editor renders all three sub-modules (Missie / Gedragsregels / Hoofddoelplanning):
// PageHeader + plain-text body (rich-text parity with legacy Summernote is out of scope this
// rung), Opslaan, "Wijzigingen ongedaan maken" (reset to last saved), and the Template action —
// a dialog over the admin policy-templates canonical library whose apply OVERWRITES the body
// (legacy semantic; the dialog warns). Hoofddoelplanning's structured variant is DEFERRED-A3 —
// built as the same free-text singleton, flagged on-screen as an open owner design question.
// Backend: GET/PUT /policy-documents/:kind · GET /policy-templates (api-client getPolicyDocument /
// savePolicyDocument / listPolicyTemplates). RLS-scoped; gated policy_document.read/.update.
import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { ApiError } from "@sympllex/api-client";
import { api } from "../../lib/api";
import { useI18n } from "../../lib/i18n";
import { useSession } from "../../lib/session";
import { hasPermission } from "@sympllex/auth";
import { PageHeader } from "@/components/page-header";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";

type PolicyDocumentKind = "mission" | "conduct" | "goal_planning";
interface PolicyDocument {
  id: string; kind: PolicyDocumentKind; regulationId: string | null;
  body: string; appliedTemplateId: string | null; version: number;
  updatedAt: string; updatedBy: string;
}
interface PolicyTemplate {
  id: string; shortCode: string; displayName: string; bodyMarkdown: string; language: string;
}

const errText = (e: unknown) => (e instanceof ApiError ? `${e.status}` : (e as Error).message);

// The three sub-module routes share this one editor — only the kind differs (snapshot §1).
export function MissionRoute() { return <PolicyDocumentEditor kind="mission" />; }
export function ConductRoute() { return <PolicyDocumentEditor kind="conduct" />; }
export function GoalPlanningRoute() { return <PolicyDocumentEditor kind="goal_planning" />; }

const TITLE_KEY: Record<PolicyDocumentKind, string> = {
  mission: "pol.mission.title", conduct: "pol.conduct.title", goal_planning: "pol.goals.title",
};
const SUB_KEY: Record<PolicyDocumentKind, string> = {
  mission: "pol.mission.sub", conduct: "pol.conduct.sub", goal_planning: "pol.goals.sub",
};

function PolicyDocumentEditor({ kind }: { kind: PolicyDocumentKind }) {
  const { t } = useI18n();
  const session = useSession();
  const canEdit = hasPermission(session, "policy_document.update");
  const qc = useQueryClient();

  // The singleton (tenant-wide: the regulation-scoped variant follows the header Verordening
  // selector, which is still a static stub — the API already accepts regulationId).
  const docQ = useQuery({
    queryKey: ["policy-document", kind],
    queryFn: () => api.getPolicyDocument(kind) as Promise<PolicyDocument | null>,
  });

  // Draft state: null = untouched (mirror the saved body); string = user-edited draft.
  const [draft, setDraft] = useState<string | null>(null);
  const [pendingTemplateId, setPendingTemplateId] = useState<string | null>(null);
  const [templateOpen, setTemplateOpen] = useState(false);

  const savedBody = docQ.data?.body ?? "";
  const body = draft ?? savedBody;
  const dirty = draft !== null && draft !== savedBody;

  const save = useMutation({
    mutationFn: () => api.savePolicyDocument(kind, {
      body,
      ...(pendingTemplateId ? { appliedTemplateId: pendingTemplateId } : {}),
    }),
    onSuccess: () => {
      setDraft(null);
      setPendingTemplateId(null);
      void qc.invalidateQueries({ queryKey: ["policy-document", kind] });
    },
  });

  const undo = () => { setDraft(null); setPendingTemplateId(null); save.reset(); };
  const applyTemplate = (tpl: PolicyTemplate) => {
    // Legacy semantic (snapshot §2.2): applying a template OVERWRITES the current text. The
    // dialog warned + asked for explicit confirmation before we get here; Opslaan persists.
    setDraft(tpl.bodyMarkdown);
    setPendingTemplateId(tpl.id);
    setTemplateOpen(false);
  };

  const bodyFieldId = `policy-document-body-${kind}`;

  return (
    <section>
      <PageHeader
        title={t(TITLE_KEY[kind])}
        description={t(SUB_KEY[kind])}
        actions={
          <Button variant="outline" disabled={!canEdit || docQ.isLoading} onClick={() => setTemplateOpen(true)}>
            {t("pol.template")}
          </Button>
        }
      />

      {kind === "goal_planning" && (
        <p className="mb-4 rounded-lg border border-dashed px-3 py-2 text-sm text-muted-foreground">
          {t("pol.goals.deferred")}
        </p>
      )}

      {docQ.error && <p className="mb-4 text-sm text-destructive">{t("crud.failed")} {errText(docQ.error)}</p>}
      {save.isError && <p className="mb-4 text-sm text-destructive">{t("pol.saveFailed")} {errText(save.error)}</p>}

      {docQ.isLoading ? (
        <Skeleton className="h-72 w-full" />
      ) : (
        <div className="space-y-3">
          <div className="flex items-center justify-between gap-3">
            <Label htmlFor={bodyFieldId}>{t("pol.body.label")}</Label>
            <div className="flex items-center gap-2">
              {pendingTemplateId && <Badge variant="secondary">{t("pol.template.applied")}</Badge>}
              {docQ.data && <span className="text-xs text-muted-foreground">{t("pol.version", docQ.data.version)}</span>}
            </div>
          </div>
          <Textarea
            id={bodyFieldId}
            value={body}
            onChange={(e) => { setDraft(e.target.value); save.reset(); }}
            disabled={!canEdit}
            placeholder={t("pol.placeholder")}
            className="min-h-72 font-normal"
          />
          <p className="text-xs text-muted-foreground">{t("pol.plaintext")}</p>
          <div className="flex flex-wrap items-center gap-2">
            {canEdit ? (
              <>
                <Button onClick={() => save.mutate()} disabled={save.isPending || (!dirty && !pendingTemplateId)}>
                  {save.isPending ? t("crud.saving") : t("common.save")}
                </Button>
                <Button variant="outline" onClick={undo} disabled={save.isPending || (!dirty && !pendingTemplateId)}>
                  {t("pol.undo")}
                </Button>
              </>
            ) : (
              <p className="text-sm text-muted-foreground">{t("crud.readonly")}</p>
            )}
            {save.isSuccess && <span className="text-sm text-green-700 dark:text-green-500">{t("crud.saved")}</span>}
          </div>
        </div>
      )}

      <TemplateDialog open={templateOpen} onOpenChange={setTemplateOpen} onApply={applyTemplate} />
    </section>
  );
}

// Template picker (snapshot §2.2/§2.5): canonical, super-tenant-authored policy templates. The
// library is owner/counsel-gated and may legitimately be EMPTY — say so honestly instead of
// pretending content exists. Applying requires an explicit confirm because it overwrites.
function TemplateDialog({
  open, onOpenChange, onApply,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onApply: (tpl: PolicyTemplate) => void;
}) {
  const { t } = useI18n();
  const [selected, setSelected] = useState<PolicyTemplate | null>(null);

  const templatesQ = useQuery({
    queryKey: ["policy-templates"],
    queryFn: () => api.listPolicyTemplates() as Promise<PolicyTemplate[]>,
    enabled: open,
  });
  const templates = templatesQ.data ?? [];

  const close = (v: boolean) => { if (!v) setSelected(null); onOpenChange(v); };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("pol.template.title")}</DialogTitle>
          <DialogDescription>{t("pol.template.warn")}</DialogDescription>
        </DialogHeader>

        {templatesQ.isLoading && <Skeleton className="h-24 w-full" />}
        {templatesQ.error && <p className="text-sm text-destructive">{t("crud.failed")} {errText(templatesQ.error)}</p>}
        {templatesQ.isSuccess && templates.length === 0 && (
          <p className="rounded-lg border border-dashed px-3 py-4 text-sm text-muted-foreground">
            {t("pol.template.empty")}
          </p>
        )}
        {templates.length > 0 && (
          <ul className="max-h-64 space-y-1 overflow-y-auto">
            {templates.map((tpl) => (
              <li key={tpl.id}>
                <button
                  type="button"
                  onClick={() => setSelected(tpl)}
                  aria-pressed={selected?.id === tpl.id}
                  className={`w-full rounded-lg border px-3 py-2 text-left text-sm ${selected?.id === tpl.id ? "border-ring bg-muted" : "border-transparent hover:bg-muted"}`}
                >
                  <span className="font-medium">{tpl.displayName}</span>
                  <span className="ml-2 text-xs text-muted-foreground">{tpl.shortCode} · {tpl.language}</span>
                </button>
              </li>
            ))}
          </ul>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => close(false)}>{t("common.cancel")}</Button>
          <Button
            variant="destructive"
            disabled={!selected}
            onClick={() => { if (selected) { onApply(selected); setSelected(null); } }}
          >
            {t("pol.template.apply")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
